import { useEffect, useState } from "react";
import { api } from "../api/client";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useAsync } from "../lib/useAsync";
import { milesBetween } from "../lib/distance";
import { levelDisplay } from "../lib/level";
import { countdownLabel, countdownTier } from "../lib/countdown";
import { MeetGraphic, accentFor } from "../components/MeetGraphic";
import { AddExternalMeet } from "../components/AddExternalMeet";
import { SkeletonList } from "../components/Skeleton";
import { groupByWeekend, weekendBucketFor, seasonEndYmd } from "../lib/weekend";
import type { DiscoveredMeet, TargetState } from "../api/types";

const STATES: TargetState[] = ["NY", "NJ", "CT", "DE", "PA", "MD"];

function AddToTeam({ meet }: { meet: DiscoveredMeet }) {
  const teams = useAsync(() => api.listTeams(), []);
  const adminTeams = teams.data?.items.filter((t) => t.role === "admin") ?? [];
  const [teamId, setTeamId] = useState("");
  const [msg, setMsg] = useState<string>();
  const [addedId, setAddedId] = useState<string>();
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const tid = teamId || adminTeams[0]?.teamId;
    if (!tid) {
      setMsg("You must admin a team first.");
      return;
    }
    setBusy(true);
    setMsg(undefined);
    try {
      const res = await api.addDiscoveredToTeam(meet.discoveryId, tid);
      setAddedId(res.meetId);
      setMsg("Added! Your team can now RSVP.");
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (adminTeams.length === 0) {
    return <span className="muted">Admin a team to add this meet.</span>;
  }
  return (
    <div className="row">
      <select
        style={{ width: "auto" }}
        value={teamId}
        onChange={(e) => setTeamId(e.target.value)}
      >
        {adminTeams.map((t) => (
          <option key={t.teamId} value={t.teamId}>
            {t.name ?? t.teamId}
          </option>
        ))}
      </select>
      <button onClick={add} disabled={busy}>
        {busy ? "Adding…" : "Add to my team"}
      </button>
      {msg && <span className="muted">{msg}</span>}
      {addedId && (
        <>
          <Link to={`/meets/${addedId}`} className="link-strong">
            View meet →
          </Link>
          <Link to="/meets" className="link-strong">
            My Meets →
          </Link>
        </>
      )}
    </div>
  );
}

export function Discover() {
  const { isAdmin } = useAuth();
  const me = useAsync(() => api.getMe(), []);
  const home =
    me.data?.homeLat !== undefined && me.data?.homeLng !== undefined
      ? { lat: me.data.homeLat, lng: me.data.homeLng }
      : undefined;
  const [state, setState] = useState("");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [sort, setSort] = useState<"date" | "state">("date");
  const [difficulty, setDifficulty] = useState(""); // "", "novice", "elite", or exact "1".."5"
  const [format, setFormat] = useState(""); // "", "WAR Zone", ...
  const [level, setLevel] = useState(""); // "", "hs", "ms", "youth"
  const [reloadKey, setReloadKey] = useState(0);
  const [groupWeekend, setGroupWeekend] = useState(true);
  const [weekendKey, setWeekendKey] = useState(""); // "" = all weekends

  const diffQuery = (): { minDifficulty?: number; maxDifficulty?: number } => {
    if (difficulty === "novice") return { maxDifficulty: 2 };
    if (difficulty === "competitive") return { minDifficulty: 3, maxDifficulty: 3 };
    if (difficulty === "elite") return { minDifficulty: 4 };
    return {};
  };

  // Default view shows only the 25 soonest-upcoming meets. Applying ANY filter
  // (state, search, from-date, difficulty, format) switches to full results.
  const DEFAULT_LIMIT = 25;
  const isFiltered = Boolean(state || q || from || difficulty || format || level);

  const { data, loading, error } = useAsync(
    () =>
      api.listDiscovered({
        state: state || undefined,
        q: q || undefined,
        from: from || undefined,
        sort,
        format: format || undefined,
        level: level || undefined,
        ...diffQuery(),
        // Weekend-grouped view loads the whole season (now → end of March) so
        // every weekend is available; otherwise fall back to the 25-meet teaser
        // unless the user has applied a filter.
        ...(groupWeekend && sort === "date"
          ? { to: seasonEndYmd() }
          : isFiltered
            ? {}
            : { limit: DEFAULT_LIMIT }),
      }),
    [state, q, from, sort, difficulty, format, level, groupWeekend, reloadKey],
  );

  // Drive-time estimates (miles + est. driving time) keyed by discoveryId,
  // fetched once per result set from the signed-in user's home ZIP. Server
  // caches per (zip, meet); returns unavailable when Maps API isn't enabled.
  const [drive, setDrive] = useState<
    Record<string, { distanceText?: string; durationText?: string; unavailable?: boolean }>
  >({});
  const [driveMsg, setDriveMsg] = useState<string>();
  useEffect(() => {
    const ids = (data?.items ?? []).map((m) => m.discoveryId);
    if (!home || ids.length === 0) {
      setDrive({});
      return;
    }
    let cancelled = false;
    api
      .driveTimes(ids)
      .then((res) => {
        if (cancelled) return;
        setDrive(res.items ?? {});
        const anyUnavailable = Object.values(res.items ?? {}).some(
          (v) => v.unavailable,
        );
        setDriveMsg(
          anyUnavailable
            ? "Driving-time estimates aren't enabled yet — showing straight-line miles."
            : undefined,
        );
      })
      .catch(() => {
        if (!cancelled) setDrive({});
      });
    return () => {
      cancelled = true;
    };
  }, [data, home]);

  return (
    <div>
      <h1>Discover meets</h1>
      <p className="muted">
        Publicly-listed wrestling meets across NY, NJ, CT, DE, PA, MD — updated
        weekly. Add one into a team you admin so your group can RSVP.{" "}
        <Link to="/meets" className="link-strong">
          ← Back to My Meets
        </Link>
        {isAdmin && (
          <>
            {" · "}
            <a href="/discovery/review">Review queue</a>{" · "}
            <a href="/discovery/sources">Source health</a>
          </>
        )}
      </p>

      {isAdmin && (
        <AddExternalMeet onAdded={() => setReloadKey((k) => k + 1)} />
      )}

      {me.data && !home && (
        <p className="muted">
          💡 Add your home ZIP in your <a href="/profile">profile</a> to see the
          distance and estimated drive to each meet.
        </p>
      )}
      {driveMsg && <p className="muted">{driveMsg}</p>}

      <div className="card">
        <div className="row">
          <select
            style={{ width: "auto" }}
            value={state}
            onChange={(e) => setState(e.target.value)}
          >
            <option value="">All states</option>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            style={{ width: "auto" }}
            value={sort}
            onChange={(e) => setSort(e.target.value as "date" | "state")}
          >
            <option value="date">Sort: Date</option>
            <option value="state">Sort: State</option>
          </select>
          <select
            style={{ width: "auto" }}
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            title="Difficulty"
          >
            <option value="">Any difficulty</option>
            <option value="novice">Novice-friendly (1–2)</option>
            <option value="competitive">Competitive (3)</option>
            <option value="elite">Elite (4–5)</option>
          </select>
          <select
            style={{ width: "auto" }}
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            title="Format"
          >
            <option value="">Any format</option>
            <option value="WAR Zone">WAR Zone / Reimagined</option>
            <option value="Duals">Duals</option>
            <option value="Open">Open</option>
            <option value="Folkstyle">Folkstyle</option>
          </select>
          <select
            style={{ width: "auto" }}
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            title="Level"
          >
            <option value="">Any level</option>
            <option value="hs">High School</option>
            <option value="ms">Middle School</option>
            <option value="youth">Youth</option>
          </select>
          <input
            type="date"
            style={{ width: "auto" }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            title="From date"
          />
        </div>
        <input
          style={{ marginTop: "0.5rem" }}
          placeholder="Search title / venue / city / source"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="row" style={{ marginTop: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          <label className="row" style={{ gap: "0.4rem" }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={groupWeekend}
              onChange={(e) => setGroupWeekend(e.target.checked)}
              disabled={sort !== "date"}
            />
            <span className="muted">
              Group by weekend
              {sort !== "date" ? " (sort by date to enable)" : ""}
            </span>
          </label>
        </div>
        {groupWeekend && sort === "date" && (
          <div className="weekend-picker" role="tablist" aria-label="Jump to weekend">
            {(() => {
              // Distinct weekends present in the current result set, with counts.
              const buckets = new Map<
                string,
                { label: string; short: string; count: number }
              >();
              for (const m of data?.items ?? []) {
                const b = weekendBucketFor(m.startDate);
                const cur = buckets.get(b.key);
                if (cur) cur.count++;
                else {
                  // Chip shows the scannable date range; relative label lives
                  // in the tooltip. ("This weekend · Nov 1–2" → "Nov 1–2")
                  const short = b.label.replace(/^.*·\s*/, "");
                  buckets.set(b.key, { label: b.label, short, count: 1 });
                }
              }
              const entries = [...buckets.entries()].sort((a, b) =>
                a[0].localeCompare(b[0]),
              );
              return (
                <>
                  <button
                    className={"wk-chip" + (weekendKey === "" ? " active" : "")}
                    onClick={() => setWeekendKey("")}
                  >
                    All
                  </button>
                  {entries.map(([key, info]) => (
                    <button
                      key={key}
                      className={"wk-chip" + (weekendKey === key ? " active" : "")}
                      title={info.label}
                      onClick={() =>
                        setWeekendKey((cur) => (cur === key ? "" : key))
                      }
                    >
                      <span className="wk-chip-label">{info.short}</span>
                      <span className="wk-chip-count">{info.count}</span>
                    </button>
                  ))}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {loading && <SkeletonList count={5} variant="disc" />}
      {error && <p className="error">{error}</p>}
      {data && (
        <p className="muted">
          {groupWeekend && sort === "date" ? (
            <>{data.count} meet(s) through end of March</>
          ) : !isFiltered && data.count >= DEFAULT_LIMIT ? (
            <>
              Showing the next {DEFAULT_LIMIT} upcoming meets — filter by state,
              date, difficulty, format, or search to see more.
            </>
          ) : (
            <>{data.count} meet(s) found</>
          )}
        </p>
      )}
      <div className="stagger">
      {(() => {
        const renderCard = (m: DiscoveredMeet) => {
        const dateLabel = new Date(`${m.startDate}T00:00:00`).toLocaleDateString(
          undefined,
          { weekday: "short", month: "short", day: "numeric", year: "numeric" },
        );
        const loc = [m.venueName, m.city].filter(Boolean).join(", ");
        const miles = milesBetween(home, {
          lat: m.venueLat,
          lng: m.venueLng,
        });
        const d = drive[m.discoveryId];
        // Distance: prefer Google's drive distance text, else haversine miles.
        const distText =
          d?.distanceText ?? (miles !== undefined ? `${miles} mi` : undefined);
        const accent = accentFor(m.format);
        // Level (always displayable): server bucket → title inference → "All levels".
        const lvl = levelDisplay(m.levelBucket, m.title, m.sessionDetails);
        const countdown = countdownLabel(m.startDate);
        const tier = countdownTier(m.startDate);
        return (
          <div
            key={m.discoveryId}
            className="card disc-card disc-interactive"
            data-tier={tier}
            style={{
              ["--card-accent" as string]: accent,
              ["--level-color" as string]: lvl.color,
            }}
          >
            <div className="disc-graphic-col">
              <MeetGraphic
                format={m.format}
                seed={m.discoveryId}
                difficulty={m.difficulty}
              />
            </div>

            <div className="disc-body">
              <div className="row spread">
                <strong className="disc-title">{m.title}</strong>
                <span className="row" style={{ gap: "0.35rem" }}>
                  {m.format && <span className="badge accent">{m.format}</span>}
                  <span
                    className="badge badge-level"
                    title={
                      lvl.inferred
                        ? "Level inferred from the event name"
                        : "Level"
                    }
                  >
                    {lvl.label}
                    {lvl.inferred ? "*" : ""}
                  </span>
                  <span className="badge state">{m.state}</span>
                </span>
              </div>

              <dl className="disc-rows">
                <div className="disc-row">
                  <dt>Date</dt>
                  <dd>
                    {dateLabel}
                    {m.startTime ? ` · ${m.startTime}` : ""}
                    {m.dateTentative ? " · (tentative)" : ""}
                    {countdown && (
                      <span className={`badge chip-countdown tier-${tier}`}>
                        {countdown}
                      </span>
                    )}
                  </dd>
                </div>

                <div className="disc-row">
                  <dt>Location</dt>
                  <dd>
                    {loc || <span className="muted">—</span>}
                    {m.sessionDetails && (
                      <span className="muted disc-sub">{m.sessionDetails}</span>
                    )}
                  </dd>
                </div>

                <div className="disc-row">
                  <dt>Distance</dt>
                  <dd>
                    {distText ? (
                      <span className="badge chip-dist">{distText}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                    {d?.durationText && (
                      <span className="badge chip-drive">
                        🚗 {d.durationText}
                      </span>
                    )}
                    {!home && !distText && (
                      <span className="muted disc-sub">
                        Add your ZIP in profile for distance
                      </span>
                    )}
                  </dd>
                </div>

                <div className="disc-row">
                  <dt>Difficulty</dt>
                  <dd>
                    {typeof m.difficulty === "number" ? (
                      <span className="badge difficulty">
                        {m.difficultyLabel
                          ? `${m.difficultyLabel} · ${m.difficulty}/5`
                          : `${m.difficulty}/5`}
                      </span>
                    ) : (
                      <span className="muted">Not rated</span>
                    )}
                  </dd>
                </div>

                <div className="disc-row">
                  <dt>Registration</dt>
                  <dd>
                    {m.registrationUrl && m.registrationStatus !== "tbd" ? (
                      <a
                        className="btn btn-sm"
                        href={m.registrationUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Register / Details
                      </a>
                    ) : (
                      <span
                        className="badge chip-hint"
                        title="No current registration link found"
                      >
                        Registration TBD
                      </span>
                    )}
                    {m.registrationInfo && (
                      <span className="muted disc-sub">
                        {m.registrationInfo}
                      </span>
                    )}
                  </dd>
                </div>

                <div className="disc-row">
                  <dt>Source</dt>
                  <dd>
                    <a
                      className="disc-source"
                      href={m.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {m.sourceName}
                    </a>
                  </dd>
                </div>
              </dl>

              <div className="disc-addteam">
                <AddToTeam meet={m} />
              </div>
            </div>
          </div>
        );
        };

        const all = data?.items ?? [];
        // Apply the weekend filter (if a specific weekend is chosen).
        const filtered = weekendKey
          ? all.filter((m) => weekendBucketFor(m.startDate).key === weekendKey)
          : all;

        if (!groupWeekend || sort !== "date") {
          return filtered.map((m) => renderCard(m));
        }
        // Grouped by weekend, chronological.
        return groupByWeekend(filtered, (m) => m.startDate).map((grp) => (
          <div key={grp.bucket.key} className="weekend-group">
            <div className="weekend-head">
              <span className="weekend-label">{grp.bucket.label}</span>
              <span className="weekend-count">
                {grp.items.length} meet{grp.items.length === 1 ? "" : "s"}
              </span>
            </div>
            {grp.items.map((m) => renderCard(m))}
          </div>
        ));
      })()}
      </div>
      {data && data.items.length === 0 && (
        <p className="muted">No discovered meets match your filters yet.</p>
      )}
    </div>
  );
}
